import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  FileText, 
  ChevronDown, 
  ChevronRight,
  TrendingUp,
  AlertTriangle,
  ChefHat,
  Users,
  BarChart3,
  RefreshCw,
  Calendar,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const AREA_ICONS = {
  operations: BarChart3,
  kitchen: ChefHat,
  front_of_house: Users
};

const AREA_COLORS = {
  operations: 'bg-blue-100 text-blue-700 border-blue-200',
  kitchen: 'bg-orange-100 text-orange-700 border-orange-200',
  front_of_house: 'bg-green-100 text-green-700 border-green-200'
};

const WeeklyReportCard = () => {
  const { currentLocation, api } = useAuth();
  const [latestReport, setLatestReport] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedArea, setExpandedArea] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [fullReportDialogOpen, setFullReportDialogOpen] = useState(false);
  const [selectedAreaForDialog, setSelectedAreaForDialog] = useState(null);

  useEffect(() => {
    if (currentLocation) {
      fetchLatestReport();
      fetchReportHistory();
    }
  }, [currentLocation]);

  const fetchLatestReport = async () => {
    setLoading(true);
    try {
      const res = await api.get('/weekly-reports/latest', { 
        params: { location_id: currentLocation.id } 
      });
      setLatestReport(res.data);
    } catch (err) {
      console.error('Error fetching latest report:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportHistory = async () => {
    try {
      const res = await api.get('/weekly-reports', { 
        params: { location_id: currentLocation.id, limit: 12 } 
      });
      setReportHistory(res.data);
    } catch (err) {
      console.error('Error fetching report history:', err);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/weekly-reports/generate', null, {
        params: { location_id: currentLocation.id }
      });
      setLatestReport(res.data);
      toast.success('Weekly report generated!');
      fetchReportHistory();
    } catch (err) {
      console.error('Error generating report:', err);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectHistoricalReport = async (reportId) => {
    try {
      const res = await api.get(`/weekly-reports/${reportId}`);
      setSelectedReport(res.data);
      setHistoryDialogOpen(false);
    } catch (err) {
      console.error('Error fetching report:', err);
      toast.error('Failed to load report');
    }
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options = { day: 'numeric', month: 'short' };
    return `${startDate.toLocaleDateString('en-GB', options)} - ${endDate.toLocaleDateString('en-GB', options)}`;
  };

  const formatCurrency = (val) => `£${(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;

  const displayReport = selectedReport || latestReport;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!displayReport) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <CardTitle>Weekly AI Report</CardTitle>
            </div>
          </div>
          <CardDescription>AI-powered weekly business review</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No Reports Yet</h3>
            <p className="text-muted-foreground mb-4 text-sm">
              Generate your first weekly report to get AI-powered insights
            </p>
            <Button onClick={handleGenerateReport} disabled={generating}>
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="weekly-report-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              <CardTitle>Weekly AI Report</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setHistoryDialogOpen(true)}
                className="text-muted-foreground"
              >
                <Calendar className="w-4 h-4 mr-1" />
                History
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleGenerateReport}
                disabled={generating}
              >
                {generating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
          <CardDescription className="flex items-center gap-2">
            <Badge variant="outline" className="font-normal">
              {formatDateRange(displayReport.week_start, displayReport.week_end)}
            </Badge>
            {selectedReport && (
              <Button 
                variant="link" 
                className="h-auto p-0 text-xs"
                onClick={() => setSelectedReport(null)}
              >
                View latest
              </Button>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {displayReport.sections?.map((section) => {
            const Icon = AREA_ICONS[section.area] || BarChart3;
            const colorClass = AREA_COLORS[section.area] || AREA_COLORS.operations;
            const isExpanded = expandedArea === section.area;
            
            return (
              <Collapsible 
                key={section.area}
                open={isExpanded}
                onOpenChange={() => setExpandedArea(isExpanded ? null : section.area)}
              >
                <Card className={`border ${isExpanded ? 'ring-1 ring-primary/20' : ''}`}>
                  <CollapsibleTrigger className="w-full">
                    <CardHeader className="py-3 px-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-md ${colorClass}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="text-left">
                            <CardTitle className="text-sm font-medium">{section.title}</CardTitle>
                            <CardDescription className="text-xs line-clamp-1 mt-0.5">
                              {section.summary}
                            </CardDescription>
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0 px-4 pb-4">
                      <div className="space-y-4">
                        {/* Highlights */}
                        {section.highlights?.length > 0 && (
                          <div>
                            <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              Key Highlights
                            </h4>
                            <ul className="space-y-1">
                              {section.highlights.map((highlight, idx) => (
                                <li key={idx} className="text-sm flex items-start gap-2">
                                  <span className="text-green-500 mt-1">•</span>
                                  <span>{highlight}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Concerns */}
                        {section.concerns?.length > 0 && section.concerns[0] !== "No specific concerns identified" && (
                          <div>
                            <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Areas of Attention
                            </h4>
                            <ul className="space-y-1">
                              {section.concerns.map((concern, idx) => (
                                <li key={idx} className="text-sm flex items-start gap-2">
                                  <span className="text-amber-500 mt-1">•</span>
                                  <span>{concern}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Key Metrics Preview */}
                        {section.key_metrics && (
                          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                            {section.area === 'operations' && (
                              <>
                                <div className="text-center p-2 bg-muted/50 rounded">
                                  <div className="text-xs text-muted-foreground">Revenue</div>
                                  <div className="font-semibold text-sm">{formatCurrency(section.key_metrics.total_revenue)}</div>
                                </div>
                                <div className="text-center p-2 bg-muted/50 rounded">
                                  <div className="text-xs text-muted-foreground">Net Profit</div>
                                  <div className={`font-semibold text-sm ${section.key_metrics.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatCurrency(section.key_metrics.net_profit)}
                                  </div>
                                </div>
                              </>
                            )}
                            {section.area === 'kitchen' && (
                              <>
                                <div className="text-center p-2 bg-muted/50 rounded">
                                  <div className="text-xs text-muted-foreground">Items Sold</div>
                                  <div className="font-semibold text-sm">{(section.key_metrics.total_items_sold || 0).toLocaleString()}</div>
                                </div>
                                <div className="text-center p-2 bg-muted/50 rounded">
                                  <div className="text-xs text-muted-foreground">Avg Food Cost</div>
                                  <div className="font-semibold text-sm">{(section.key_metrics.avg_food_cost_pct || 0).toFixed(1)}%</div>
                                </div>
                              </>
                            )}
                            {section.area === 'front_of_house' && (
                              <>
                                <div className="text-center p-2 bg-muted/50 rounded">
                                  <div className="text-xs text-muted-foreground">Avg Check</div>
                                  <div className="font-semibold text-sm">{formatCurrency(section.key_metrics.avg_check)}</div>
                                </div>
                                <div className="text-center p-2 bg-muted/50 rounded">
                                  <div className="text-xs text-muted-foreground">Labor Cost %</div>
                                  <div className="font-semibold text-sm">{(section.key_metrics.labor_cost_pct || 0).toFixed(1)}%</div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* Read Full Report Button */}
                        <Button 
                          variant="link" 
                          className="h-auto p-0 text-xs"
                          onClick={() => {
                            setSelectedAreaForDialog(section);
                            setFullReportDialogOpen(true);
                          }}
                        >
                          Read full analysis →
                        </Button>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </CardContent>
      </Card>

      {/* History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report History</DialogTitle>
            <DialogDescription>View previous weekly reports</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {reportHistory.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No reports yet</p>
            ) : (
              reportHistory.map((report) => (
                <Button
                  key={report.id}
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => handleSelectHistoricalReport(report.id)}
                >
                  <span>{formatDateRange(report.week_start, report.week_end)}</span>
                  <Badge variant="secondary" className="text-xs">
                    {new Date(report.generated_at).toLocaleDateString('en-GB')}
                  </Badge>
                </Button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Report Dialog */}
      <Dialog open={fullReportDialogOpen} onOpenChange={setFullReportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedAreaForDialog?.title}</DialogTitle>
            <DialogDescription>
              {formatDateRange(displayReport?.week_start, displayReport?.week_end)}
            </DialogDescription>
          </DialogHeader>
          <div className="prose prose-sm max-w-none">
            <div className="whitespace-pre-wrap text-sm">
              {selectedAreaForDialog?.full_report}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WeeklyReportCard;
